"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.STATUS_TRACKED_TRIGGER_TYPES = exports.AutomationWatchedStatusColumnError = exports.AUTOMATION_WATCHED_STATUS_COLUMN = exports.RowChangedConfigSchema = exports.ACTIVATABLE_TRIGGER_TYPES = exports.TriggerTypeSchema = exports.TRIGGER_TYPES = void 0;
exports.isActivatableTrigger = isActivatableTrigger;
exports.isRowTrigger = isRowTrigger;
exports.watchedStatusColumnMessage = watchedStatusColumnMessage;
exports.watchesStatusColumn = watchesStatusColumn;
exports.assertWatchedColumns = assertWatchedColumns;
exports.trackedStatusColumnId = trackedStatusColumnId;
exports.effectiveWatchedColumns = effectiveWatchedColumns;
exports.hasConfiguredAutomationInput = hasConfiguredAutomationInput;
exports.needsStatusTracking = needsStatusTracking;
exports.statusTrackingGap = statusTrackingGap;
const zod_1 = require("zod");
/**
 * Persisted automation trigger kinds (AutomationSchedule.triggerType and the
 * block's `triggerConfig.type`).
 *
 * - `manual`: runs only on an explicit run.
 * - `scheduled`: cron preset; switched on/off (Activate / Pause).
 * - `webhook`: inbound workspace webhook credential; switched on/off like
 *   `scheduled`, calls are admitted only while it is on.
 * - `row_created` ("Bei neuer Zeile"): event-driven, a row of the source
 *   table is created after the automation was switched on.
 * - `row_changed` ("Zeile geändert"): event-driven, a watched column of a
 *   source-table row changes.
 *
 * Row triggers are events only: which rows run is decided by the input
 * filter group alone, repetitions are limited by status tracking as for a
 * schedule. They require a source table, have no cron preset and no webhook
 * credential; status tracking is optional. Switched on/off like `scheduled`.
 */
exports.TRIGGER_TYPES = [
    'manual',
    'scheduled',
    'webhook',
    'row_created',
    'row_changed',
];
exports.TriggerTypeSchema = zod_1.z.enum(exports.TRIGGER_TYPES);
/** Trigger types whose automation is switched on and off by the user. */
exports.ACTIVATABLE_TRIGGER_TYPES = [
    'scheduled',
    'row_created',
    'row_changed',
    'webhook',
];
function isActivatableTrigger(type) {
    return !!type && exports.ACTIVATABLE_TRIGGER_TYPES.includes(type);
}
/** Triggers fired by row events of the automation's source table. */
function isRowTrigger(type) {
    return type === 'row_created' || type === 'row_changed';
}
/**
 * `row_changed` settings of the block's triggerConfig. An empty list
 * watches every column except the status tracking column. The list is
 * bounded by the columns of the source table.
 */
exports.RowChangedConfigSchema = zod_1.z
    .object({ watchedColumnIds: zod_1.z.array(zod_1.z.string().min(1)) })
    .strict();
exports.AUTOMATION_WATCHED_STATUS_COLUMN = 'AUTOMATION_WATCHED_STATUS_COLUMN';
/**
 * Why the status tracking column cannot be watched, in German and English,
 * naming the column by its name where the caller knows it.
 */
function watchedStatusColumnMessage(columnName) {
    return (`Die Status-Tracking-Spalte „${columnName}“ kann nicht überwacht werden, die Automation schreibt sie selbst. / ` +
        `The status tracking column “${columnName}” cannot be watched; the automation writes it itself.`);
}
/** The status tracking column was chosen as a watched column. */
class AutomationWatchedStatusColumnError extends Error {
    constructor(columnId) {
        super(watchedStatusColumnMessage(columnId));
        this.columnId = columnId;
        this.code = exports.AUTOMATION_WATCHED_STATUS_COLUMN;
        this.name = 'AutomationWatchedStatusColumnError';
    }
}
exports.AutomationWatchedStatusColumnError = AutomationWatchedStatusColumnError;
/** Whether the watched list names the status tracking column. */
function watchesStatusColumn(watchedColumnIds, statusColumnId) {
    return !!statusColumnId && watchedColumnIds.includes(statusColumnId);
}
/**
 * The status tracking column is never watched: a run writes it, so watching
 * it would start the automation again. Every server write of watched columns
 * or of the status column checks this.
 */
function assertWatchedColumns(watchedColumnIds, statusColumnId) {
    if (watchesStatusColumn(watchedColumnIds, statusColumnId)) {
        throw new AutomationWatchedStatusColumnError(statusColumnId);
    }
}
/**
 * The column status tracking writes: set only while tracking is on in
 * status-column mode (delete mode writes no column).
 */
function trackedStatusColumnId(statusTracking) {
    if (!statusTracking?.enabled || statusTracking.mode === 'delete_row') {
        return null;
    }
    return statusTracking.columnId ?? null;
}
/**
 * Columns whose change fires `row_changed`: the watched list (empty = all
 * columns of the source table) limited to the current schema, without the
 * status tracking column.
 */
function effectiveWatchedColumns(watchedColumnIds, schemaColumnIds, statusColumnId) {
    const schema = new Set(schemaColumnIds);
    const watched = watchedColumnIds.length === 0
        ? [...schema]
        : watchedColumnIds.filter(columnId => schema.has(columnId));
    return [...new Set(watched)].filter(columnId => columnId !== statusColumnId);
}
/**
 * Whether the automation's input is ready to start a run. A webhook needs
 * its credential and a row trigger its source table; otherwise the
 * input table is optional: without one the workflow runs once per trigger
 * (schedule or manual run) without input rows.
 */
function hasConfiguredAutomationInput(config) {
    if (!config)
        return false;
    switch (config.triggerConfig?.type) {
        case 'webhook':
            return !!config.triggerConfig.webhookCredentialId;
        case 'row_created':
        case 'row_changed':
            return !!config.sourceTableId;
        default:
            return true;
    }
}
/** Triggers that run on their own over a source table and mark its rows. */
exports.STATUS_TRACKED_TRIGGER_TYPES = [
    'scheduled',
    'row_created',
    'row_changed',
];
/**
 * Automatic runs over a source table (schedule, new row, changed row) mark
 * every row they take: the pending value when the run starts, then the
 * success or error value. Rows with either value are not taken again by a
 * later run. Without a source table a schedule runs once per tick; manual
 * runs and webhooks run for the rows they are given and use it optionally.
 */
function needsStatusTracking(config) {
    const type = config?.triggerConfig?.type;
    return (!!config?.sourceTableId &&
        !!type &&
        exports.STATUS_TRACKED_TRIGGER_TYPES.includes(type));
}
function statusTrackingGap(config) {
    const tracking = config?.statusTracking;
    const needed = needsStatusTracking(config);
    if (!tracking?.enabled)
        return needed ? 'off' : null;
    if (tracking.mode === 'delete_row')
        return null;
    if (!tracking.columnId)
        return 'column';
    if (needed &&
        (!tracking.pendingValue?.trim() || !tracking.errorValue?.trim())) {
        return 'values';
    }
    return null;
}
//# sourceMappingURL=trigger.js.map